import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { AdvancedSortService, SortRule } from '../services/advanced-sort.service';
import { FilterService } from '../services/filter.service';
import { IFilterConfig, IFilterRule, ColumnOption, SearchSortConfig } from '../services/interfaces';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-sort-modal',
    standalone: true,
    imports: [CommonModule, FormsModule, DragDropModule, MatIconModule, MatButtonModule],
    templateUrl: './sort-modal.component.html',
    styleUrls: ['./sort-modal.component.scss']
})
export class SortModalComponent implements OnInit, OnDestroy {
    @Input() isOpen: boolean = false;
    @Input() availableColumns: ColumnOption[] = [];
    @Input() focusedColumn: ColumnOption | null = null;
    @Input() filterConfigs: IFilterConfig[] = [];
    @Output() close = new EventEmitter<void>();
    @Output() applySort = new EventEmitter<SearchSortConfig>();

    // Sort properties
    sortRules: SortRule[] = [];
    selectedColumnForSort: string = '';
    selectedOrder: 'asc' | 'desc' = 'asc';
    
    // Search properties
    searchTerm: string = '';
    caseSensitive: boolean = false;
    exactMatch: boolean = false;
    focusedSearchFields: string[] = [];
    
    // Filter properties
    filterRules: IFilterRule[] = [];
    selectedColumnForFilter: string = '';
    filterValue: any = null;
    filterDateFrom: Date | null = null;
    filterDateTo: Date | null = null;
    
    private subscription: Subscription;

    constructor(
        public advancedSortService: AdvancedSortService,
        private filterService: FilterService
    ) {}

    ngOnInit(): void {
        this.subscription = this.advancedSortService.sortRules$.subscribe(rules => {
            this.sortRules = [...rules];
        });
        this.advancedSortService.availableColumns$.subscribe(columns => { if (columns?.length) 
            { this.availableColumns = columns;}
        });
        
        console.log('?? Sort Modal - Available Columns:', this.availableColumns);
        console.log('?? Sort Modal - Filter Configs:', this.filterConfigs);
        console.log('?? Sort Modal - Filterable Columns:', this.availableColumnsForFilter);
        
        // Handle focused column if provided
        if (this.focusedColumn) {
            // Pre-select the column for sorting
            this.selectedColumnForSort = this.focusedColumn.name;
            
            // Set focused search fields to only this column
            this.focusedSearchFields = [this.focusedColumn.name];
            
            // Auto-add this column to sort if not already sorted
            const existingRule = this.sortRules.find(r => r.columnName === this.focusedColumn!.name);
            if (!existingRule) {
                // Pre-select ascending order
                this.selectedOrder = 'asc';
            }
        }
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    /**
     * Get available columns (excluding already added ones)
     */
    get availableColumnsForAdd(): ColumnOption[] {
        const usedColumns = this.sortRules.map(r => r.columnName);
        return this.availableColumns.filter(c => 
            c.sortable && !usedColumns.includes(c.name)
        );
    }

    /**
     * Get filterable columns (excluding already filtered ones)
     */
    get availableColumnsForFilter(): ColumnOption[] {
        const usedColumns = this.filterRules.map(r => r.columnName);
        const filterableColumns = this.availableColumns.filter(c => 
            c.filterable && !usedColumns.includes(c.name)
        );
        
        // Debug logging
        if (filterableColumns.length === 0 && this.filterRules.length === 0) {
            console.warn('?? No filterable columns found!');
            console.log('Available columns:', this.availableColumns);
            console.log('Filterable check:', this.availableColumns.map(c => ({
                name: c.name,
                filterable: c.filterable,
                filterType: c.filterType
            })));
        }
        
        return filterableColumns;
    }

    /**
     * Get searchable fields
     */
    get searchFields(): string[] {
        return this.availableColumns
            .filter(c => c.sortable) // All sortable columns are searchable
            .map(c => c.name);
    }

    /**
     * Get search fields text for display
     */
    getSearchFieldsText(): string {
        if (this.focusedColumn) {
            return this.focusedColumn.displayName;
        }
        
        const displayNames = this.availableColumns
            .filter(c => c.sortable)
            .map(c => c.displayName);
        
        if (displayNames.length <= 3) {
            return displayNames.join(', ');
        }
        
        return `${displayNames.slice(0, 3).join(', ')} and ${displayNames.length - 3} more`;
    }

    /**
     * Clear focus and search all columns
     */
    clearFocus(): void {
        this.focusedColumn = null;
        this.focusedSearchFields = [];
        this.selectedColumnForSort = '';
    }

    /**
     * Handle search change
     */
    onSearchChange(): void {
        console.log('Search changed:', {
            term: this.searchTerm,
            caseSensitive: this.caseSensitive,
            exactMatch: this.exactMatch,
            focusedColumn: this.focusedColumn?.name
        });
    }

    /**
     * Clear search
     */
    clearSearch(): void {
        this.searchTerm = '';
        this.caseSensitive = false;
        this.exactMatch = false;
        this.onSearchChange();
    }

    /**
     * Add new sort rule
     */
    addRule(): void {
        if (!this.selectedColumnForSort) {
            return;
        }

        const column = this.availableColumns.find(c => c.name === this.selectedColumnForSort);
        if (column) {
            this.advancedSortService.addSortRule(
                column.name,
                column.displayName,
                this.selectedOrder
            );
            this.selectedColumnForSort = '';
        }
    }

    /**
     * Remove sort rule
     */
    removeRule(ruleId: string): void {
        this.advancedSortService.removeSortRule(ruleId);
    }

    /**
     * Toggle rule enabled/disabled
     */
    toggleRule(ruleId: string): void {
        this.advancedSortService.toggleSortRule(ruleId);
    }

    /**
     * Update sort order
     */
    updateOrder(ruleId: string, order: 'asc' | 'desc'): void {
        this.advancedSortService.updateSortOrder(ruleId, order);
    }

    /**
     * Handle drag and drop to reorder
     */
    onDrop(event: CdkDragDrop<SortRule[]>): void {
        const rules = [...this.sortRules];
        moveItemInArray(rules, event.previousIndex, event.currentIndex);
        this.advancedSortService.reorderSortRules(rules);
    }

    /**
     * Move rule up in priority
     */
    moveUp(index: number): void {
        if (index > 0) {
            const rules = [...this.sortRules];
            [rules[index - 1], rules[index]] = [rules[index], rules[index - 1]];
            this.advancedSortService.reorderSortRules(rules);
        }
    }

    /**
     * Move rule down in priority
     */
    moveDown(index: number): void {
        if (index < this.sortRules.length - 1) {
            const rules = [...this.sortRules];
            [rules[index], rules[index + 1]] = [rules[index + 1], rules[index]];
            this.advancedSortService.reorderSortRules(rules);
        }
    }

    /**
     * Clear all rules
     */
    clearAll(): void {
        if (confirm('Are you sure you want to clear all sorting rules?')) {
            this.advancedSortService.clearAllRules();
        }
    }

    /**
     * Reset to defaults
     */
    resetToDefaults(): void {
        if (confirm('Reset all search, filter and sort settings to defaults?')) {
            this.clearSearch();
            this.advancedSortService.clearAllRules();
            this.filterRules = [];
        }
    }

    /**
     * Apply sorting, filtering and search, then close modal
     */
    apply(): void {
        const searchFields = this.focusedColumn 
            ? [this.focusedColumn.name]
            : this.searchFields;
            
        const config: SearchSortConfig = {
            searchTerm: this.searchTerm,
            caseSensitive: this.caseSensitive,
            exactMatch: this.exactMatch,
            searchFields: searchFields,
            sortRules: this.sortRules.filter(r => r.enabled),
            filterRules: this.filterRules.filter(r => r.enabled)
        };
        
        this.applySort.emit(config);
        this.onClose();
    }

    /**
     * Close modal
     */
    onClose(): void {
        this.close.emit();
    }
    
    /**
     * Alias for onClose to match template
     */
    closeModal(): void {
        this.onClose();
    }

    /**
     * Get sort summary
     */
    getSortSummary(): string {
        return this.advancedSortService.getSortSummary();
    }

    // ==================== FILTER METHODS ====================

    /**
     * Get available columns for filtering (excluding already filtered ones)
     */
    get availableColumnsForFilterDropdown(): ColumnOption[] {
        const usedColumns = this.filterRules.map(r => r.columnName);
        return this.availableColumns.filter(c => 
            c.filterable && !usedColumns.includes(c.name)
        );
    }

    /**
     * Get filter type for a column
     */
    getColumnFilterType(columnName: string): 'boolean' | 'text' | 'number' | 'date' | 'dateRange' | 'dropdown' {
        const column = this.availableColumns.find(c => c.name === columnName);
        return column?.filterType || 'text';
    }

    /**
     * Get dropdown options for a column � from ColumnOption.options resolved via configKey
     */
    getDropdownOptions(columnName: string): { label: string; value: any }[] {
        const column = this.availableColumns.find(c => c.name === columnName);
        if (column?.options?.length) {
            return column.options;
        }
        // Fall back to filterConfigs (manually supplied options)
        const config = this.filterConfigs?.find(f => f.name === columnName);
        return config?.options ?? [];
    }

    /**
     * Handle filter column selection change
     */
    onFilterColumnChange(): void {
        // Reset filter values when column changes
        this.filterValue = null;
        this.filterDateFrom = null;
        this.filterDateTo = null;
    }

    /**
     * Add filter rule
     */
    addFilterRule(): void {
        if (!this.selectedColumnForFilter) {
            return;
        }
        
        const column = this.availableColumns.find(c => c.name === this.selectedColumnForFilter);
        if (!column) {
            return;
        }
        
        const filterType = column.filterType || 'text';
        
        // Create filter rule object
        const newRule: IFilterRule = {
            id: this.generateId(),
            columnName: this.selectedColumnForFilter,
            displayName: column.displayName,
            filterType: filterType,
            value: this.filterValue,
            dateFrom: this.filterDateFrom,
            dateTo: this.filterDateTo,
            enabled: true
        };
        
        // Validate the filter
        const validation = this.filterService.validateFilter(newRule);
        if (!validation.valid) {
            alert(validation.message);
            return;
        }
        
        this.filterRules.push(newRule);
        
        // Clear form
        this.selectedColumnForFilter = '';
        this.filterValue = null;
        this.filterDateFrom = null;
        this.filterDateTo = null;
    }

    /**
     * Remove filter rule
     */
    removeFilterRule(ruleId: string): void {
        const index = this.filterRules.findIndex(r => r.id === ruleId);
        if (index > -1) {
            this.filterRules.splice(index, 1);
        }
    }

    /**
     * Toggle filter rule enabled/disabled
     */
    toggleFilterRule(ruleId: string): void {
        const rule = this.filterRules.find(r => r.id === ruleId);
        if (rule) {
            rule.enabled = !rule.enabled;
        }
    }

    /**
     * Clear all filters
     */
    clearAllFilters(): void {
        if (confirm('Clear all filters?')) {
            this.filterRules = [];
        }
    }

    /**
     * Get display value for a filter rule
     */
    getFilterValueDisplay(rule: IFilterRule): string {
        if (rule.filterType === 'boolean') {
            return rule.value ? 'True (Active)' : 'False (Inactive)';
        } else if (rule.filterType === 'dateRange') {
            const fromDate = rule.dateFrom ? new Date(rule.dateFrom).toLocaleDateString() : '';
            const toDate = rule.dateTo ? new Date(rule.dateTo).toLocaleDateString() : '';
            return `${fromDate} to ${toDate}`;
        } else if (rule.filterType === 'date') {
            return rule.value ? new Date(rule.value).toLocaleDateString() : '';
        } else if (rule.filterType === 'dropdown') {
            const options = this.getDropdownOptions(rule.columnName);
            const match = options.find(o => o.value == rule.value);
            return match ? match.label : rule.value?.toString() || '';
        } else {
            return rule.value?.toString() || '';
        }
    }

    /**
     * Generate unique ID
     */
    private generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }
}
